(() => {
  'use strict';

  const NAMESPACE = 'camoVariantUrlContext';
  if (window[NAMESPACE]) return;

  const PARAMS = {
    type: {
      query: 'filter_type',
      selector: '.xo-product-info-variant--type',
      values: new Map([
        ['wraps', 'Wraps'],
        ['cones', 'Cones']
      ]),
      includeSubtitle: true
    },
    packType: {
      query: 'filter_pack_type',
      selector: '.xo-product-info-variant--pack-type',
      values: new Map([
        ['boxes', 'Boxes'],
        ['pouches', 'Pouches']
      ]),
      includeSubtitle: false
    }
  };

  const ACTIVE_CLASS = 'xo-product-info-variant--camo-url-context-active';
  const HIDDEN_CLASS = 'xo-field-radio--camo-url-context-hidden';
  const COMPACT_GROUP_CLASS = 'xo-product-info-variant--camo-url-context-static';
  const HEADER_CLASS = 'xo-product-info-content__header--camo-url-context';
  const SUMMARY_CLASS = 'camo-variant-url-context-summary';
  let context = readContext(window.location.href);
  let observer = null;
  let applyQueued = false;
  let selectionSyncQueued = false;
  let selectionSyncRunning = false;

  function normalize(value) {
    return String(value || '').trim().toLocaleLowerCase('en-US');
  }

  function readContext(urlValue) {
    let url;
    try {
      url = new URL(urlValue, window.location.href);
    } catch (_error) {
      return {};
    }

    const next = {};
    Object.entries(PARAMS).forEach(([key, config]) => {
      const normalized = normalize(url.searchParams.get(config.query));
      if (config.values.has(normalized)) next[key] = config.values.get(normalized);
    });
    return next;
  }

  function findValueLabel(group, allowedValue) {
    return Array.from(group.querySelectorAll('label.xo-field-radio')).find((label) => {
      const input = label.querySelector('input[type="radio"]');
      return input && normalize(input.value) === normalize(allowedValue);
    }) || null;
  }

  function readableText(element) {
    if (!element) return '';
    return String(element.innerText || element.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function readCardContent(label) {
    return {
      title: readableText(label.querySelector('.xo-field-radio__card-title')),
      subtitle: readableText(label.querySelector('.xo-field-radio__card-subtitle'))
    };
  }

  function resetPresentation() {
    document.querySelectorAll(`.${SUMMARY_CLASS}`).forEach((summary) => summary.remove());
    document.querySelectorAll(`.${HEADER_CLASS}`).forEach((header) => header.classList.remove(HEADER_CLASS));
    document.querySelectorAll(`.${COMPACT_GROUP_CLASS}`).forEach((group) => {
      group.classList.remove(COMPACT_GROUP_CLASS);
      group.removeAttribute('aria-hidden');
    });
  }

  function resetGroup(group) {
    group.classList.remove(ACTIVE_CLASS);
    group.querySelectorAll(`.${HIDDEN_CLASS}`).forEach((label) => {
      label.classList.remove(HIDDEN_CLASS);
      label.removeAttribute('aria-hidden');
    });
  }

  function applyGroup(config, allowedValue) {
    const summaries = [];

    document.querySelectorAll(config.selector).forEach((group) => {
      resetGroup(group);
      if (!allowedValue) return;

      const allowedLabel = findValueLabel(group, allowedValue);
      if (!allowedLabel) return;

      const labels = Array.from(group.querySelectorAll('label.xo-field-radio'));
      const labelsToHide = labels.filter((label) => label !== allowedLabel);

      // A valid URL context should also compact a naturally single-value option.
      // This is required for live products whose Type and/or Pack Type were
      // consolidated to one value after the original multi-value testing.
      const cardContent = readCardContent(allowedLabel);
      if (!cardContent.title) return;

      labelsToHide.forEach((label) => {
        label.classList.add(HIDDEN_CLASS);
        label.setAttribute('aria-hidden', 'true');
      });

      group.classList.add(ACTIVE_CLASS, COMPACT_GROUP_CLASS);
      group.setAttribute('aria-hidden', 'true');
      summaries.push({
        group,
        title: cardContent.title,
        subtitle: config.includeSubtitle ? cardContent.subtitle : ''
      });
    });

    return summaries;
  }

  function addSummaryItem(summary, item) {
    const separatorBefore = document.createElement('span');
    separatorBefore.className = 'camo-variant-url-context-summary__separator';
    separatorBefore.setAttribute('aria-hidden', 'true');
    separatorBefore.textContent = '|';

    const value = document.createElement('span');
    value.className = 'camo-variant-url-context-summary__value';
    value.textContent = item.title;

    summary.append(separatorBefore, value);

    if (item.subtitle) {
      const separatorAfter = separatorBefore.cloneNode(true);
      const subtitle = document.createElement('span');
      subtitle.className = 'camo-variant-url-context-summary__subtitle';
      subtitle.textContent = item.subtitle;
      summary.append(separatorAfter, subtitle);
    }
  }

  function renderSummaries(items) {
    const itemsByContent = new Map();

    items.forEach((item) => {
      const content = item.group.closest('.xo-product-info-content');
      if (!content) return;
      if (!itemsByContent.has(content)) itemsByContent.set(content, []);
      itemsByContent.get(content).push(item);
    });

    itemsByContent.forEach((contentItems, content) => {
      const header = content.querySelector('.xo-product-info-content__header');
      const title = header?.querySelector('.xo-product-info-content__title');
      if (!header || !title) return;

      const summary = document.createElement('span');
      summary.className = SUMMARY_CLASS;
      summary.style.setProperty(
        '--camo-variant-url-context-title-size',
        window.getComputedStyle(title).fontSize
      );

      contentItems
        .sort((a, b) => {
          const position = a.group.compareDocumentPosition(b.group);
          return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        })
        .forEach((item) => addSummaryItem(summary, item));

      title.insertAdjacentElement('afterend', summary);
      header.classList.add(HEADER_CLASS);
    });
  }

  function singleAvailableValue(config) {
    const groups = Array.from(document.querySelectorAll(config.selector));
    if (!groups.length) return '';

    const values = new Set();
    groups.forEach((group) => {
      group.querySelectorAll('label.xo-field-radio input[type="radio"]').forEach((input) => {
        const normalized = normalize(input.value);
        if (config.values.has(normalized)) values.add(config.values.get(normalized));
      });
    });

    return values.size === 1 ? Array.from(values)[0] : '';
  }

  function findContextInput(config, value) {
    if (!value) return null;

    for (const group of document.querySelectorAll(config.selector)) {
      const input = Array.from(group.querySelectorAll('input[type="radio"]')).find(
        (candidate) => normalize(candidate.value) === normalize(value)
      );
      if (input) return input;
    }
    return null;
  }

  function activateInput(input) {
    if (!input || input.checked || input.disabled) return false;

    // Use the native interaction path so every existing product-page listener
    // updates price, media, availability, hidden variant ID, and the address bar.
    const label = input.closest('label.xo-field-radio');
    if (label) label.click();
    else input.click();
    return true;
  }

  function syncContextSelection() {
    if (selectionSyncRunning || (!context.type && !context.packType)) return;
    selectionSyncRunning = true;

    const steps = [
      [PARAMS.type, context.type],
      [PARAMS.packType, context.packType]
    ].filter(([, value]) => Boolean(value));

    const runStep = (index) => {
      if (index >= steps.length) {
        selectionSyncRunning = false;
        queueApply();
        return;
      }

      const [config, value] = steps[index];
      const input = findContextInput(config, value);
      const changed = activateInput(input);

      // Type changes can rebuild or reconfigure Pack Type, so allow the theme's
      // normal variant machinery to finish before locating the next option.
      window.setTimeout(() => runStep(index + 1), changed ? 80 : 0);
    };

    runStep(0);
  }

  function queueSelectionSync() {
    if (selectionSyncQueued) return;
    selectionSyncQueued = true;
    window.requestAnimationFrame(() => {
      selectionSyncQueued = false;
      syncContextSelection();
    });
  }

  function apply() {
    resetPresentation();

    // A Type URL is enough to activate the URL-context presentation. When the
    // product naturally has only one Pack Type, infer it from the rendered
    // option instead of requiring filter_pack_type in the URL as well.
    const inferredPackType = context.packType || (context.type ? singleAvailableValue(PARAMS.packType) : '');

    const summaries = [
      ...applyGroup(PARAMS.type, context.type),
      ...applyGroup(PARAMS.packType, inferredPackType)
    ];
    renderSummaries(summaries);
  }

  function queueApply() {
    if (applyQueued) return;
    applyQueued = true;
    window.requestAnimationFrame(() => {
      applyQueued = false;
      apply();
    });
  }

  function preserveContextInUrl(urlValue) {
    if (!urlValue || (!context.type && !context.packType)) return urlValue;

    let url;
    try {
      url = new URL(urlValue, window.location.href);
    } catch (_error) {
      return urlValue;
    }

    if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return urlValue;

    if (context.type) url.searchParams.set(PARAMS.type.query, context.type);
    if (context.packType) url.searchParams.set(PARAMS.packType.query, context.packType);

    if (typeof urlValue === 'string' && !/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(urlValue)) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  }

  function patchHistory(methodName) {
    const original = history[methodName];
    if (typeof original !== 'function') return;

    history[methodName] = function patchedHistory(state, title, url) {
      const result = original.call(this, state, title, preserveContextInUrl(url));
      queueApply();
      return result;
    };
  }

  function handlePopState() {
    context = readContext(window.location.href);
    queueSelectionSync();
    queueApply();
  }

  function handleVariantChange(event) {
    if (event.target.matches('.xo-product-info-variant input[type="radio"]')) queueApply();
  }

  function startObserver() {
    if (!document.body || observer) return;
    observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('.xo-product-info-variant, xo-product') || node.querySelector?.('.xo-product-info-variant'))
        )
      );
      if (relevant) {
        queueSelectionSync();
        queueApply();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window[NAMESPACE] = { apply };
  patchHistory('pushState');
  patchHistory('replaceState');
  window.addEventListener('popstate', handlePopState);
  window.addEventListener('pageshow', () => {
    queueSelectionSync();
    queueApply();
  });
  document.addEventListener('change', handleVariantChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      queueSelectionSync();
      apply();
      startObserver();
    }, { once: true });
  } else {
    queueSelectionSync();
    apply();
    startObserver();
  }
})();
